import React, { useEffect, useState } from "react";
import { Check, Loader2, Zap, Building2, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { getPlans, startCheckout } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const PLAN_META = {
  free: { name: "Free", price: 0, points: 3, features: ["3 render points (one-time)", "Unlimited AI estimates", "AI assistant chat", "PDF export & share links"], icon: Sparkles },
  pro_monthly: { name: "Pro", price: 29, points: 50, features: ["50 render points / month", "Unlimited saved projects", "Blueprint → 3D studio", "Cost trends & material swaps", "Priority estimates"], icon: Zap, popular: true },
  business_monthly: { name: "Business", price: 99, points: 200, features: ["200 render points / month", "Everything in Pro", "Multi-angle compare", "Team-ready share links", "Priority support"], icon: Building2 },
};

const Pricing = () => {
  const { user, login } = useAuth();
  const { toast } = useToast();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    getPlans().then((d) => setPacks(d.packs || [])).catch(() => {});
  }, []);

  const buy = async (lookupKey) => {
    if (!user) {
      toast({ title: "Sign in to continue", description: "Redirecting you to Google sign-in…" });
      setTimeout(login, 800);
      return;
    }
    setBusy(lookupKey);
    try {
      const { checkout_url } = await startCheckout(lookupKey);
      window.location.href = checkout_url;
    } catch (e) {
      toast({ title: "Couldn't start checkout", description: "Please try again." });
      setBusy(null);
    }
  };

  const planKeys = ["free", "pro_monthly", "business_monthly"];

  return (
    <section id="pricing" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm font-semibold uppercase tracking-wider text-[#DC2626]">Pricing</span>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl font-700 tracking-tight text-[#0A2540]">
            Simple plans, powered by points
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Estimates are always free. Photorealistic 3D renders use points — pick a plan or top up anytime.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {planKeys.map((key) => {
            const p = PLAN_META[key];
            const Icon = p.icon;
            const isFree = key === "free";
            return (
              <div
                key={key}
                className={`relative rounded-2xl border bg-white p-8 flex flex-col ${
                  p.popular ? "border-[#1D4ED8] shadow-xl ring-1 ring-[#1D4ED8]" : "border-slate-200"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#1D4ED8] text-white text-xs font-semibold px-3 py-1">
                    Most popular
                  </span>
                )}
                <div className="flex items-center gap-2.5">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${p.popular ? "bg-[#1D4ED8]" : "bg-[#0A2540]"} text-white`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-display text-xl font-700 text-[#0A2540]">{p.name}</span>
                </div>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-display text-4xl font-700 text-[#0A2540]">${p.price}</span>
                  <span className="text-slate-500 mb-1">/ month</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-slate-700">
                      <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#DC2626]/10 shrink-0">
                        <Check className="h-3 w-3 text-[#DC2626]" />
                      </span>
                      <span className="text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                {isFree ? (
                  <Button onClick={() => (user ? null : login())} variant="outline" className="mt-8 w-full border-slate-300 text-[#0A2540]" disabled={!!user}>
                    {user ? "Your base plan" : "Start free"}
                  </Button>
                ) : (
                  <Button onClick={() => buy(key)} disabled={busy === key} className={`mt-8 w-full ${p.popular ? "bg-[#1D4ED8] hover:bg-[#1e40af]" : "bg-[#DC2626] hover:bg-[#b91c1c]"} text-white`}>
                    {busy === key ? <Loader2 className="h-4 w-4 animate-spin" /> : `Upgrade to ${p.name}`}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Point packs */}
        <div className="mt-14">
          <h3 className="text-center font-display text-2xl font-700 text-[#0A2540]">Need more renders? Top up points</h3>
          <div className="mt-8 grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {packs.map((pack) => (
              <div key={pack.lookup_key} className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
                <div className="font-display text-3xl font-700 text-[#0A2540]">{pack.points}</div>
                <div className="text-sm text-slate-500">render points</div>
                <div className="mt-3 font-semibold text-[#1D4ED8]">${pack.price}</div>
                <Button onClick={() => buy(pack.lookup_key)} disabled={busy === pack.lookup_key} variant="outline" className="mt-4 w-full border-[#1D4ED8] text-[#1D4ED8] hover:bg-blue-50">
                  {busy === pack.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy points"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
